// Artemis-shaped example data for the in-browser mock of services/api, seen
// from the installer side. Every figure is an example. JobRow / InstallRow
// follow services/api/src/rows.ts; the installer extras (address, product,
// party) are the columns crm.v_job carries for the portal.
import { WorkDocument, type Case, type WorkRecord, type WorkRevision } from "@artemis-group/aiw-contracts";
import type { Install, WorkOrder } from "../lib/types";
import roundTripDocument from "../../../crm/src/mock/work-record-round-trip.json";

const NOW = new Date();
export const iso = (dayOffset: number, hh: number, mm = 0) => {
  const d = new Date(NOW); d.setDate(d.getDate() + dayOffset); d.setHours(hh, mm, 0, 0); return d.toISOString();
};
const origin = { data_origin: "live" as const, test_set_id: null };

export const YU = { id: "yu-flooring", name: "YU Flooring" };
export const TA = { id: null, name: "Team Artemis" };

export type MockRole = "external" | "internal";
export function mockRole(): MockRole {
  try {
    const v = localStorage.getItem("aiw.installer.mockRole");
    if (v === "internal" || v === "external") return v;
  } catch { /* private mode */ }
  return (import.meta.env?.VITE_AIW_MOCK_ROLE as string | undefined) === "internal" ? "internal" : "external";
}

export function me(role: MockRole) {
  return role === "external"
    ? {
        user: { id: "22222222-2222-4222-8222-222222222222", email: "yu@yuflooring.example.test" },
        actor: { kind: "human" as const, id: "22222222-2222-4222-8222-222222222222", role: "installer_external", org_id: YU.id },
        memberships: [{ role: "installer_external", org_id: YU.id, status: "active" }],
        party: { id: YU.id, name: YU.name },
        installer_member: { user_id: "22222222-2222-4222-8222-222222222222", display_name: "Company Manager", team_role: "manager" as const, can_install: false, preferred_language: "en" as const, active: true, phone: "+61400000001", email: null },
        surface: "installer" as const,
        test_set_id: null as string | null,
      }
    : {
        user: { id: "33333333-3333-4333-8333-333333333333", email: "crew2@artemisflooring.com" },
        actor: { kind: "human" as const, id: "33333333-3333-4333-8333-333333333333", role: "installer_internal", org_id: null },
        memberships: [{ role: "installer_internal", org_id: null, status: "active" }],
        party: { id: "team-artemis", name: TA.name },
        installer_member: null,
        surface: "installer" as const,
        test_set_id: null as string | null,
      };
}

/** crm.v_job rows plus the portal columns. `party_id` null = a Team Artemis crew. */
export const jobs = [
  { ref: "J092610210", name: "Marsh · Narrabeen", raw_stage: "Installation Scheduled", effective_stage: "Installation Scheduled", stage_group: "Fulfillment", outcome: "won", deal_value: null, contact_id: "c-0010", account_id: null, suburb: "Narrabeen", creation_date: iso(-25, 0), close_date: null, install_gate: "Scheduled", install_next_date: iso(0, 7, 0), lead_id: null, workflow_flags: {}, created_at: iso(-25, 0), updated_at: iso(-1, 0),
    address: "38 Ocean St, Narrabeen NSW 2101", product: "Topdeck Aqua Hybrid 'Spotted Gum'", area_m2: 62, stairs: 14, customer: "Aiden Marsh", customer_phone: "0402 ••• 118", party_id: YU.id, party_name: YU.name, crew: "YU Flooring", installation_id: "i-210", portal_state: "installing", answer_by: null, ...origin },
  { ref: "J092610231", name: "Chen · Castle Hill", raw_stage: "Pending Schedule", effective_stage: "Pending Schedule", stage_group: "Fulfillment", outcome: "won", deal_value: null, contact_id: "c-0009", account_id: null, suburb: "Castle Hill", creation_date: iso(-20, 0), close_date: null, install_gate: "Offered", install_next_date: iso(7, 7, 0), lead_id: null, workflow_flags: {}, created_at: iso(-20, 0), updated_at: iso(0, 7, 5),
    address: "14 Excelsior Ave, Castle Hill NSW 2154", product: "Wonderwood Ultra Hybrid 'Smoked Oak'", area_m2: 84, stairs: 14, customer: "Wei Chen", customer_phone: "0422 ••• 901", party_id: YU.id, party_name: YU.name, crew: "YU Flooring", installation_id: "i-231", portal_state: "offered", answer_by: iso(2, 17, 0), ...origin },
  { ref: "J082610188", name: "Liu · Lane Cove", raw_stage: "Invoiced", effective_stage: "Invoiced", stage_group: "Invoicing", outcome: "completed", deal_value: null, contact_id: "c-0005", account_id: null, suburb: "Lane Cove", creation_date: iso(-40, 0), close_date: iso(-5, 0), install_gate: "Install Complete", install_next_date: null, lead_id: null, workflow_flags: {}, created_at: iso(-40, 0), updated_at: iso(-5, 0),
    address: "18 Burns Bay Rd, Lane Cove NSW 2066", product: "Repair · hybrid", area_m2: 6, stairs: 0, customer: "Grace Liu", customer_phone: "0400 ••• 515", party_id: YU.id, party_name: YU.name, crew: "YU Flooring", installation_id: "i-188", portal_state: "done", signed_off_at: iso(-5, 15, 0), answer_by: null, ...origin },
  { ref: "J092610218", name: "Raman · Epping", raw_stage: "Installation Scheduled", effective_stage: "Installation Scheduled", stage_group: "Fulfillment", outcome: "won", deal_value: null, contact_id: "c-0006", account_id: null, suburb: "Epping", creation_date: iso(-11, 9, 0), close_date: null, install_gate: "Scheduled", install_next_date: iso(4, 7, 0), lead_id: null, workflow_flags: {}, created_at: iso(-11, 9, 0), updated_at: iso(-2, 10, 0),
    address: "22 Rawson St, Epping NSW 2121", product: "Preference Oakleaf HD Plus 'Fumed Oak'", area_m2: 58.2, stairs: 14, customer: "Priya Raman", customer_phone: "0401 ••• 774", party_id: null, party_name: TA.name, crew: "Team Artemis · Crew 2", installation_id: "i-218", portal_state: "scheduled", answer_by: null, ...origin },
  { ref: "J092610203", name: "Whitlam · Mosman", raw_stage: "Installation Scheduled", effective_stage: "Installation Scheduled", stage_group: "Fulfillment", outcome: "won", deal_value: null, contact_id: "c-0011", account_id: null, suburb: "Mosman", creation_date: iso(-30, 0), close_date: null, install_gate: "Scheduled", install_next_date: iso(0, 7, 30), lead_id: null, workflow_flags: {}, created_at: iso(-30, 0), updated_at: iso(-1, 0),
    address: "3 Raglan St, Mosman NSW 2088", product: "Signature 'Milano Pebble' carpet", area_m2: 41, stairs: 0, customer: "Sophie Whitlam", customer_phone: "0411 ••• 302", party_id: null, party_name: TA.name, crew: "Team Artemis · Crew 1", installation_id: "i-203", portal_state: "installing", answer_by: null, ...origin },
  { ref: "J082610169", name: "Reid · Hurstville", raw_stage: "Fully Paid", effective_stage: "Fully Paid", stage_group: "Completed", outcome: "completed", deal_value: null, contact_id: "c-0012", account_id: null, suburb: "Hurstville", creation_date: iso(-55, 0), close_date: iso(-7, 0), install_gate: "Install Complete", install_next_date: null, lead_id: null, workflow_flags: {}, created_at: iso(-55, 0), updated_at: iso(-5, 0),
    address: "7 Forest Rd, Hurstville NSW 2220", product: "Signature 'Ashford Twist' carpet", area_m2: 38, stairs: 0, customer: "Mark Reid", customer_phone: "0415 ••• 660", party_id: null, party_name: TA.name, crew: "Team Artemis · Crew 2", installation_id: "i-169", portal_state: "done", signed_off_at: iso(-7, 16, 0), answer_by: null, ...origin },
];

export const installs: Install[] = [
  { id: "i-210", job_ref: "J092610210", team: "YU Flooring", gate: "Scheduled", next_date: iso(0, 7, 0), schedule_col_count: 2, created_at: iso(-10, 0), updated_at: iso(0, 7, 10), days: [
    { id: "day-210-1", date: iso(0, 7, 0), crew: "YU Flooring", task: "Living, hall, 3 bedrooms · 62 m²", done: null },
    { id: "day-210-2", date: iso(1, 7, 0), crew: "YU Flooring", task: "Stairs · 14 nosings · finish and clean", done: null },
  ], ...origin },
  { id: "i-231", job_ref: "J092610231", team: "YU Flooring", gate: "Offered", next_date: iso(7, 7, 0), schedule_col_count: 2, created_at: iso(-1, 0), updated_at: iso(0, 7, 5), days: [
    { id: "day-231-1", date: iso(7, 7, 0), crew: "YU Flooring", task: "Living / dining, kitchen, hall · 52 m²", done: null },
    { id: "day-231-2", date: iso(8, 7, 0), crew: "YU Flooring", task: "Bedrooms · 32 m² · stairs · 14 nosings", done: null },
  ], ...origin },
  { id: "i-188", job_ref: "J082610188", team: "YU Flooring", gate: "Install Complete", next_date: null, schedule_col_count: 1, created_at: iso(-12, 0), updated_at: iso(-5, 0), days: [
    { date: iso(-6, 8, 0), crew: "YU Flooring", task: "Repair · replace 4 planks · hall", done: iso(-6, 12, 0) },
  ], ...origin },
  { id: "i-218", job_ref: "J092610218", team: "Team Artemis · Crew 2", gate: "Scheduled", next_date: iso(4, 7, 0), schedule_col_count: 3, created_at: iso(-3, 0), updated_at: iso(-1, 0), days: [
    { date: iso(4, 7, 0), crew: "Team Artemis · Crew 2", task: "Living + hall · Fumed Oak", done: null },
    { date: iso(5, 7, 0), crew: "Team Artemis · Crew 2", task: "Bedrooms · Fumed Oak", done: null },
    { date: iso(6, 7, 0), crew: "Team Artemis · Crew 2", task: "Stairs · 14 treads + nosing", done: null },
  ], ...origin },
  { id: "i-203", job_ref: "J092610203", team: "Team Artemis · Crew 1", gate: "Scheduled", next_date: iso(0, 7, 30), schedule_col_count: 1, created_at: iso(-12, 0), updated_at: iso(-1, 0), days: [
    { date: iso(0, 7, 30), crew: "Team Artemis · Crew 1", task: "Carpet · 3 rooms", done: null },
  ], ...origin },
  { id: "i-169", job_ref: "J082610169", team: "Team Artemis · Crew 2", gate: "Install Complete", next_date: null, schedule_col_count: 1, created_at: iso(-20, 0), updated_at: iso(-7, 0), days: [
    { date: iso(-8, 7, 30), crew: "Team Artemis · Crew 2", task: "Carpet · 38 m²", done: iso(-8, 14, 0) },
  ], ...origin },
];

export const workOrders: WorkOrder[] = [
  {
    job_ref: "J092610210", wo_ref: "WO-0210", address: "38 Ocean St, Narrabeen NSW 2101", customer: "Aiden Marsh", customer_phone: "0402 ••• 118", start_time: iso(0, 7, 0),
    parking: "Street parking on Ocean St · unload at the side gate, 12 m to the living room · no stairs to carry",
    access: "Key safe 4471 · dog in yard, friendly",
    ppe: ["Hi-Vis", "Steel cap", "Hard hat"],
    office_contact: { name: "Jessie", phone: "0487 ••• 212" },
    notes: "Start the living room from the front wall and run planks along the hall so the line carries into the bedrooms. 10 mm expansion gap all walls, hidden by scotia. Stairs on day 2: 14 nosings, aluminium, supplied. Undercut the three door jambs. Customer wants the offcuts left in the garage.",
    materials: [
      { item: "Topdeck Aqua Hybrid · Spotted Gum", detail: "1830 × 183 · 6.5 mm", qty: "29 pk · 67 m²" },
      { item: "Underlay 2 mm", detail: "included with hybrid", qty: "4 rolls" },
      { item: "Stair nosing · alu · Spotted Gum match", detail: null, qty: "14 × 1.0 m" },
      { item: "Scotia · colour match", detail: null, qty: "18 × 2.4 m" },
      { item: "Door trims · flat cover", detail: null, qty: "5" },
    ],
    delivered_at: iso(-3, 10, 0), measured_at: iso(-14, 10, 0),
    rooms: [
      { name: "Living", dim: "5.9 × 4.1", area_m2: 24.2 }, { name: "Hall", dim: "4.8 × 1.1", area_m2: 5.3 }, { name: "Bed 1", dim: "3.9 × 3.4", area_m2: 13.3 }, { name: "Bed 2", dim: "3.3 × 3.1", area_m2: 10.2 }, { name: "Bed 3", dim: "3.0 × 3.0", area_m2: 9.0 },
    ],
    plan: {
      rooms: [
        { x: 20, y: 20, w: 200, h: 120, name: "Living", dim: "5.9 × 4.1 m", area: "24.2 m²", hl: true },
        { x: 220, y: 20, w: 120, h: 70, name: "Kitchen · tile stays", dim: "", area: "" },
        { x: 220, y: 90, w: 120, h: 50, name: "Hall", dim: "4.8 × 1.1", area: "5.3 m²" },
        { x: 20, y: 140, w: 110, h: 95, name: "Bed 1", dim: "3.9 × 3.4 m", area: "13.3 m²" },
        { x: 130, y: 140, w: 110, h: 95, name: "Bed 2", dim: "3.3 × 3.1 m", area: "10.2 m²" },
        { x: 240, y: 140, w: 100, h: 95, name: "Bed 3", dim: "3.0 × 3.0", area: "9.0 m²" },
      ],
      doors: [[220, 100, 220, 130], [60, 140, 84, 140], [170, 140, 194, 140], [280, 140, 304, 140]],
      start: [24, 26, 24, 134], direction: "across",
    },
    pm: { name: "Jessie", after_day: 2 },
    labour: [
      { line: "Floor", qty: "62 m²", rate: "$22 / m²", amount: 1364 },
      { line: "Stairs", qty: "14", rate: "$60 per step", amount: 840 },
      { line: "Removal", qty: "62 m²", rate: "$8 / m²", amount: 496 },
    ],
  },
  {
    job_ref: "J092610231", wo_ref: "WO-0231", address: "14 Excelsior Ave, Castle Hill NSW 2154", customer: "Wei Chen", customer_phone: "0422 ••• 901", start_time: iso(7, 7, 0),
    parking: "Driveway parking · unload through the garage", access: "Key safe · code on the work order on the day", ppe: ["Hi-Vis", "Steel cap"],
    office_contact: { name: "Jessie", phone: "0487 ••• 212" },
    notes: "Stairs are closed, nosing per step, same as Narrabeen. Colour sample confirmed by the Chens on 5 Sep.",
    materials: [
      { item: "Wonderwood Ultra Hybrid · Smoked Oak", detail: "1520 × 228 · 6 mm", qty: "42 pk · 92 m²" },
      { item: "Scotia · colour match", detail: null, qty: "4 × 2.4 m" },
    ],
    delivered_at: null, measured_at: iso(-18, 10, 0),
    rooms: [
      { name: "Living / dining", dim: "6.4 × 5.2", area_m2: 33.3 }, { name: "Kitchen", dim: "3.8 × 3.6", area_m2: 13.7 }, { name: "Hall", dim: "4.1 × 1.2", area_m2: 4.9 }, { name: "Bed 1", dim: "4.2 × 3.9", area_m2: 16.4 }, { name: "Bed 2", dim: "3.6 × 3.3", area_m2: 11.9 }, { name: "Bed 3", dim: "3.4 × 3.1", area_m2: 10.5 },
    ],
    plan: {
      rooms: [
        { x: 20, y: 20, w: 180, h: 130, name: "Living / dining", dim: "6.4 × 5.2 m", area: "33.3 m²", hl: true },
        { x: 200, y: 20, w: 110, h: 90, name: "Kitchen", dim: "3.8 × 3.6 m", area: "13.7 m²" },
        { x: 200, y: 110, w: 110, h: 40, name: "Hall", dim: "4.1 × 1.2", area: "4.9 m²" },
        { x: 20, y: 150, w: 120, h: 90, name: "Bed 1", dim: "4.2 × 3.9 m", area: "16.4 m²" },
        { x: 140, y: 150, w: 95, h: 90, name: "Bed 2", dim: "3.6 × 3.3 m", area: "11.9 m²" },
        { x: 235, y: 150, w: 75, h: 90, name: "Bed 3", dim: "3.4 × 3.1", area: "10.5 m²" },
      ],
      doors: [[200, 60, 200, 84], [230, 150, 254, 150], [170, 150, 194, 150], [60, 150, 84, 150]],
      start: [24, 26, 24, 144], direction: "across",
    },
    pm: { name: "Jessie", after_day: 2 },
    labour: [
      { line: "Floor", qty: "84 m²", rate: "$22 / m²", amount: 1848 },
      { line: "Stairs", qty: "14", rate: "$60 per step", amount: 840 },
    ],
  },
  {
    job_ref: "J092610218", wo_ref: "WO-0218", address: "22 Rawson St, Epping NSW 2121", customer: "Priya Raman", customer_phone: "0401 ••• 774", start_time: iso(4, 7, 0),
    parking: "Driveway · two cars · unload at the front door", access: "Priya home all three days", ppe: ["Hi-Vis", "Steel cap"],
    office_contact: { name: "Jessie", phone: "0487 ••• 212" },
    notes: "Fumed Oak runs along the hall. Carpet removed 5 Sep. Stairs day 3: 14 treads, nosing per step. Set-out check before the first row.",
    materials: [
      { item: "Preference Oakleaf HD Plus · Fumed Oak", detail: "1900 × 190 · 14 mm", qty: "29 pk · 62 m²" },
      { item: "Stair nosing · timber · Fumed Oak", detail: null, qty: "14 × 1.0 m" },
      { item: "Scotia · Fumed Oak", detail: null, qty: "16 × 2.4 m" },
    ],
    delivered_at: null, measured_at: iso(-14, 10, 0),
    rooms: [
      { name: "Living", dim: "5.6 × 4.3", area_m2: 24.1 }, { name: "Dining", dim: "3.6 × 3.0", area_m2: 10.8 }, { name: "Hall", dim: "3.9 × 1.1", area_m2: 4.3 }, { name: "Bed 1", dim: "3.8 × 3.2", area_m2: 12.2 }, { name: "Bed 2", dim: "3.4 × 2.0", area_m2: 6.8 },
    ],
    plan: {
      rooms: [
        { x: 20, y: 20, w: 170, h: 130, name: "Living", dim: "5.6 × 4.3 m", area: "24.1 m²", hl: true },
        { x: 190, y: 20, w: 110, h: 90, name: "Dining", dim: "3.6 × 3.0 m", area: "10.8 m²" },
        { x: 190, y: 110, w: 110, h: 40, name: "Hall", dim: "3.9 × 1.1", area: "4.3 m²" },
        { x: 20, y: 150, w: 120, h: 90, name: "Bed 1", dim: "3.8 × 3.2 m", area: "12.2 m²" },
        { x: 140, y: 150, w: 100, h: 90, name: "Bed 2", dim: "3.4 × 2.0 m", area: "6.8 m²" },
        { x: 240, y: 150, w: 60, h: 90, name: "Bath", dim: "tile", area: "" },
      ],
      doors: [[190, 60, 190, 84], [60, 150, 84, 150], [170, 150, 194, 150], [300, 120, 300, 140]],
      start: [24, 26, 24, 144], direction: "across",
    },
    pm: { name: "Jessie", after_day: 3 },
    labour: null,
  },
  {
    job_ref: "J092610203", wo_ref: "WO-0203", address: "3 Raglan St, Mosman NSW 2088", customer: "Sophie Whitlam", customer_phone: "0411 ••• 302", start_time: iso(0, 7, 30),
    parking: "Street parking on Raglan St · 2 h limit, move the van at 9:30", access: "Sophie home in the morning · back door after 12", ppe: ["Hi-Vis"],
    office_contact: { name: "Jessie", phone: "0487 ••• 212" },
    notes: "Three bedrooms, Milano Pebble. Old carpet already out. Gripper is in; check the smooth-edge at the ensuite door.",
    materials: [
      { item: "Signature Milano Pebble · broadloom", detail: "3.66 m roll", qty: "12.4 lm" },
      { item: "Underlay · 10 mm foam", detail: null, qty: "41 m²" },
    ],
    delivered_at: iso(-1, 9, 0), measured_at: iso(-20, 10, 0),
    rooms: [{ name: "Bed 1", dim: "4.1 × 3.6", area_m2: 14.8 }, { name: "Bed 2", dim: "3.7 × 3.4", area_m2: 12.6 }, { name: "Bed 3", dim: "3.9 × 3.5", area_m2: 13.6 }],
    plan: {
      rooms: [
        { x: 20, y: 20, w: 150, h: 110, name: "Bed 1", dim: "4.1 × 3.6 m", area: "14.8 m²", hl: true },
        { x: 170, y: 20, w: 170, h: 110, name: "Bed 2", dim: "3.7 × 3.4 m", area: "12.6 m²" },
        { x: 20, y: 130, w: 150, h: 105, name: "Bed 3", dim: "3.9 × 3.5 m", area: "13.6 m²" },
        { x: 170, y: 130, w: 170, h: 105, name: "Hall · existing tile", dim: "", area: "" },
      ],
      doors: [[170, 60, 170, 84], [60, 130, 84, 130], [230, 130, 254, 130]],
      start: null, direction: "down",
    },
    pm: { name: "Jessie", after_day: 1 },
    labour: null,
  },
];

export const sharedWorkRecord: WorkRecord = {
  id: "10000000-0000-4000-8000-000000000001",
  stage: "job",
  opportunity_ref: "O-00000042",
  job_ref: "J092610210",
  title: "Marsh · Narrabeen",
  updated_at: iso(-1, 16, 20),
};

export const sharedWorkRevisions: WorkRevision[] = [{
  id: "30000000-0000-4000-8000-000000000001",
  work_id: sharedWorkRecord.id,
  owner_user_id: "20000000-0000-4000-8000-000000000001",
  owner_name: "Jacky",
  save_number: 2,
  saved_at: iso(-1, 16, 20),
  device: { id: "ipad-siteintel", name: "Jacky's iPad", surface: "mobile" },
  idempotency_key: "mobile-save-0002",
  content_hash: "735585a44f1e2c993a285f2e69d751c19d599bd9ca384a7ba2203ac2520378d6",
  source: null,
  document: WorkDocument.parse({ ...roundTripDocument, simple_plank_sections: { "quoted-product:simple:mock-floor": [{
    id: "40000000-0000-4000-8000-000000000001", name: "Living", runLengthMM: 4000,
    widthMM: 400, wallGapMM: 5, boardLengthMM: 1515, boardWidthMM: 195,
    rows: [{ id: "row-1", measuredRunMM: 4010, source: "laser" },
      { id: "row-2", measuredRunMM: 3570, source: "laser" }],
    savedLayDiagram: { longestMeasuredRunMM: 4010, rows: [
      { rowNumber: 1, measuredRunMM: 4010, widthMM: 195, needsOnsiteSetOut: false,
        pieces: [{ kind: "startCut", serial: "1-A", fromStartWallMM: 5, lengthMM: 1000 },
          { kind: "fullBoard", fromStartWallMM: 1005, lengthMM: 1515 },
          { kind: "endCut", serial: "1-B", fromStartWallMM: 2520, lengthMM: 1485 }] },
      { rowNumber: 2, measuredRunMM: 3570, widthMM: 195, needsOnsiteSetOut: false,
        pieces: [{ kind: "startCut", serial: "2-A", fromStartWallMM: 5, lengthMM: 700 },
          { kind: "fullBoard", fromStartWallMM: 705, lengthMM: 1515 },
          { kind: "endCut", serial: "2-B", fromStartWallMM: 2220, lengthMM: 1345 }] },
    ] },
  }] } }),
}];

const office = { kind: "agent" as const, id: "scheduling-desk", role: "agent" as const, org_id: null };

/** One offered job, delivered to the installer surface. The verbs are the
 *  installer's (Accept / Ask… / Decline); the card never shows approval words. */
export const cases: Case[] = [
  {
    id: "c0000000-0000-4000-8000-000000000101", action_id: "ac000000-0000-4000-8000-000000000101", command: "schedule_install", radius: 2,
    proposed_by: office,
    questions: {
      what: { kind: "text", body: `New job offered: Chen, Castle Hill. ${new Date(iso(7, 7)).toLocaleDateString("en-AU", { weekday: "short", day: "numeric" })} – ${new Date(iso(8, 7)).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}, 84 m² Wonderwood hybrid plus 14 stairs, two days. The job goes on your list for both days; the work order opens when the materials land.` },
      why_now: { trigger: "Team Artemis is booked that week and you did the last two Castle Hill jobs.", run_id: null },
      considered: { reasoning: "Accept both days · materials land the Friday before; key safe and parking on the work order.", rejected: ["Accept day 1 only · the office finds a second crew for the stairs", "Decline · not available · the office offers it to another crew today"], confidence: 0.8 },
      touches: { recipients: ["YU Flooring"], amount_aud: null, tables: ["public.installations"] },
      if_no: "The office offers the job to another crew; nothing else changes for you.",
      undo: { kind: "with_effort", path: "call the office" },
    },
    deliver_to: { surface: "installer", channel: "inline", audience: ["installer_external"] }, status: "pending", created_at: iso(0, 7, 5), expires_at: iso(2, 17, 0),
  },
];

export const installerTeam = [
  { kind: "member" as const, party_id: YU.id, user_id: "22222222-2222-4222-8222-222222222222", display_name: "Company Manager", team_role: "manager" as const, can_install: false, preferred_language: "en" as const, active: true, phone: "+61400000001", email: null, state: "active" },
  { kind: "member" as const, party_id: YU.id, user_id: "44444444-4444-4444-8444-444444444444", display_name: "Example Installer", team_role: "worker" as const, can_install: true, preferred_language: "zh" as const, active: true, phone: "+61400000002", email: null, state: "active" },
];

export const installerTeamWork = installs
  .filter((install) => jobs.some((job) => job.installation_id === install.id && job.party_id === YU.id))
  .flatMap((install) => (install.days ?? []).filter((d) => d.id && !d.done).map((d) => ({
    installation_id: install.id,
    job_ref: install.job_ref,
    day_id: d.id!,
    date: d.date,
    task: d.task,
    party_id: YU.id,
    company: YU.name,
    workers: d.id === "day-210-1" ? [{ user_id: "44444444-4444-4444-8444-444444444444", name: "Example Installer" }] : [],
  })));

/** Own labour, own jobs, running balance. External parties only. */
export const statement = {
  party: YU,
  month: NOW.toLocaleDateString("en-AU", { month: "long" }),
  month_zh: NOW.toLocaleDateString("zh-CN", { month: "long" }),
  owed: 6820,
  paid: { amount: 4120, at: iso(-4, 0) },
  pending: 2700,
  lines: [
    { job_ref: "J082610203", job: "Whitlam · Mosman", work: "41 m² carpet · 2 days", amount: 2460, state: "paid" as const },
    { job_ref: "J082610188", job: "Liu · Lane Cove", work: "Repair · 1 day", amount: 660, state: "paid" as const },
    { job_ref: "PRJ-26100", job: "Darlington", work: "Days 1–2 · 70 m²", amount: 1000, state: "paid" as const },
    { job_ref: "J092610210", job: "Marsh · Narrabeen", work: "62 m² + 14 stairs + removal", amount: 2700, state: "pending" as const },
  ],
  statements: [
    { id: "ST-2026-09", total: null, draft: true },
    { id: "ST-2026-08", total: 9310, draft: false },
    { id: "ST-2026-07", total: 7880, draft: false },
  ],
  rates: [
    { key: "rate_hybrid" as const, rate: "$22 / m²", agreed: "Jul 2026" },
    { key: "rate_carpet" as const, rate: "$30 / m²", agreed: null },
    { key: "rate_stairs" as const, rate: "$60", per_step: true, agreed: null },
    { key: "rate_removal" as const, rate: "$8 / m²", agreed: null },
  ],
};
